import { createFileRoute } from '@tanstack/react-router'
import { Lock } from 'lucide-react'
import { NotFound } from '@/components/custom/not-found'
import { requireDealReviewAccess } from '@/features/deal-review/access'
import { DealLeadInbox } from '@/components/custom/deal-review/deal-lead-inbox'
import {
  useUserStore,
  selectCapabilities,
  selectIsProfileReady,
} from '@/stores/user.store'

/**
 * The Deal Review screen.
 *
 * Behind `VITE_DEAL_REVIEW_ENABLED`, on in dev and off in prod, so the module
 * can keep shipping without gating unrelated releases.
 *
 * That flag is a release control and not an access control. Deal review is
 * Greybull-internal and the backend answers 404 to every external identity and
 * 403 to an internal user without the `deal-review` grant, which is what
 * actually enforces it: hidden UI is not a security boundary and the server is
 * the one that decides.
 */
export const Route = createFileRoute('/_authenticated/deal-review')({
  beforeLoad: requireDealReviewAccess,
  component: DealReviewRoute,
})

/**
 * Checked here and not in `beforeLoad`: the profile is loaded by the
 * `_authenticated` component, which has not run when a child's `beforeLoad`
 * does, so an early check would deny a legitimate user on a cold load.
 */
function DealReviewRoute() {
  const capabilities = useUserStore(selectCapabilities)
  const isProfileReady = useUserStore(selectIsProfileReady)

  if (!isProfileReady) {
    return null
  }

  if (!capabilities.includes('deal-review:view')) {
    return <DealReviewNoAccess />
  }

  return <DealLeadInbox />
}

function DealReviewNoAccess() {
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <NotFound
        useCard
        icon={<Lock strokeWidth={1} className="size-12 text-primary/70" />}
        title="No access to Deal Review"
        description="A Greybull admin can turn this on for you under App access in User Management."
        onActionLabel="Go back to home"
        className="w-full max-w-md flex-none gap-5 bg-background p-10 md:p-10"
        titleClassName="text-xl"
      />
    </div>
  )
}
